import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';

// One shared empty set for the defaults below. A fresh `new Set()` per render
// is a new dependency for the node layer's rebuild, which then redraws every
// node on any render of this component — including every zoom tick.
const NO_IDS = new Set();
import PathwayCanvas from './PathwayCanvas';
import PathwayNodeLayer from './PathwayNodeLayer';
import LoopLinkLayer from './LoopLinkLayer';
import LoopEdgeLayer from './LoopEdgeLayer';
import CrossLinkLayer from './CrossLinkLayer';
import FocusTownCaptions from './FocusTownCaptions';
import { focusArrows } from './focusArrows';
import ChoiceBoxLayer from './ChoiceBoxLayer';
import BlockerLayer from './BlockerLayer';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';
import { isStraightPair } from './straightPair';
import { loopColor } from './loopVisuals';
import { PathwayArrowLayer } from './PathwayArrow';
import ClusterConnectionLayer from './ClusterConnectionLayer';
import { applyLayout, LAYOUT_TYPES } from './layouts';
import { routeArrowPath } from './layouts/route';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { pathwaysToLayoutFormat } from '../utils/pathwayDataTransform';
import { buildSeam, seamColours } from '../utils/seam';
import { mergeConnectionsIntoPathways } from '../../utils/pathwayGraph';
import { blockerCardHeight, blockerStackLayout } from './blockerCardMetrics';
import { EFFECT_TYPES, effectColor, DEFAULT_EFFECT_TYPE, normalizeEffectType } from '../../config/effectTypes';
import { valenceStyle } from '../../config/valence';

/**
 * PathwayRenderer - Main coordinator for pathway visualization
 *
 * Responsibilities:
 * - Transform data to layout format
 * - Apply layout algorithm
 * - Coordinate rendering layers (arrows + nodes)
 * - Handle user interactions
 * - Calculate content bounds for auto-fit
 * - Expose pan methods via ref
 */
/**
 * Everything a blocker cuts off.
 *
 * A blocker sits on an edge and stops the flow there, so the consequence it
 * points at — and everything that followed from that consequence — is cut, not
 * merely annotated. Marking the whole reach in dark red is the honest picture:
 * this part of the map does not happen while the blocker stands.
 *
 * This is also the set the disarmament module reverses (PRD §7): removing the
 * blocker lights the same subtree back up.
 */
const CUT_COLOR = '#8B2C2C';

function buildCutNodes(nodes, blockers) {
  if (!blockers?.length) return new Set();
  const childrenBy = {};
  nodes.forEach((n) => {
    (n.parentIds || []).forEach((parentId) => {
      (childrenBy[parentId] = childrenBy[parentId] || []).push(n.id);
    });
  });
  const cut = new Set();
  const stack = blockers.filter((b) => b.childNodeId).map((b) => b.childNodeId);
  while (stack.length) {
    const id = stack.pop();
    if (cut.has(id)) continue;
    cut.add(id);
    (childrenBy[id] || []).forEach((childId) => stack.push(childId));
  }
  return cut;
}

const PathwayRenderer = forwardRef(function PathwayRenderer({
  showChoices = false,      // Valinnat: boxes around each fork (§4b)
  choicePointIds = null,    // ids of nodes that fork into wanted and unwanted
  choicePoints = [],        // the forks themselves, with their wanted/unwanted tallies
  pathways,
  connections = [], // Multi-parent edges (pathway_connections) — merged into parentIds below
  width,
  height,
  layoutType = LAYOUT_TYPES.ORGANIC_TREE,
  onNodeClick,
  onNodeUpdate,
  onZoomChange,
  onAddConsequenceClick, // New prop for (+) button clicks
  currentParticipantId, // Current user's participant ID for highlighting own nodes
  highlightedNodeId, // Node to highlight (e.g., parent when adding consequence)
  processingNodeIds, // Set of node IDs being AI-processed (show glimmer)
  pulsingNodeId = null,
  freshHighlightNodeIds = NO_IDS,
  clusters = [], // Semantic clusters from RAG service for visualization
  showClusterConnections = false, // Toggle for cluster connection visualization
  nodeTypeLabels = null,
  // Impact Path v2: draw each edge's effect type on the path, colour the arrow
  // by it, and let the label be tapped to change it. Off for v1.
  showEdgeTypes = false,
  colorEdgesByType = true,
  onEdgeLabelClick = null,
  // Blockers pinned to edges, and the knot that adds them (v2 §4).
  blockers = [],
  onEdgeKnotClick = null,
  onBlockerClick = null,
  // Links with a blocker at one end (impact_path_links). A blocker only ever
  // blocks, so these are drawn in red and push their target downstream.
  blockerLinks = [],
  // "What actually gets through?" — drop everything downstream of a blocker so
  // the map ends where the flow does.
  hideBlockedDownstream = false,
  // 'loops' | 'cross' | 'blockers' | null — keep only what takes part, plus the
  // chains that lead to it, so the map still hangs together.
  focus = null,
  // Node ids an undecided AI proposal touches: the card carries a small mark so
  // the queue is visible where the work is, not only in the bar.
  suggestedNodeIds = null,
  // { key, ids, from, to, label, color, chain?, loop? } — the connector being
  // looked at.
  selection = null,
  onSelectEdge = null,
  // The seam (§4j): sama asia groups drawn once as merged roots above the
  // walls, members marked at their tips, no line between them. `scenarios`
  // gives the marks their wall colours; a tap on a mark reaches onSeamTap.
  scenarios = [],
  onSeamTap = null,
  seamMutualOnly = false,
  // The chain that relation runs along, laid out on its own. Two ids for an
  // edge, the whole ring for a loop.
  focusChain = null,
  focusLabel = null,
  focusColor = null,
  focusLoop = null,
  // Mode-driven: hide the (+) when the phase is not about adding consequences.
  showAddButtons = true,
  // Loop drawing: a handle on each card that drags a link to another card.
  showLinkHandles = false,
  onDrawLink = null,
  loops = [],
  onLoopClick = null,
  // Cross-branch links (pathway_connections that are not a node's primary
  // parent). Off by default: they are true, and they are also the fastest way
  // to turn a readable tree into a hairball.
  showGlobalLinks = false,
  onCrossLinkTypeClick = null,
  // Elastic layout: transient per-node x-offsets, e.g. to open a gap where a
  // new node is about to land. Never persisted — the underlying layout is
  // untouched, so nothing reflows for good (anti-reflow rule).
  nodeOffsets = null,
  animatePositions = false,
  glideKey = null, // a change of view: cards glide (PathwayNodeLayer), connectors fade in after
}, ref) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  // One card and one gutter for the layout, the connectors and the focus view
  const CARD_WIDTH = NODE_CARD_WIDTH;
  const COLUMN_GAP = showEdgeTypes ? 340 : 200;
  const [positions, setPositions] = useState({});
  const [zoomK, setZoomK] = useState(1);
  const [arrows, setArrows] = useState([]);
  const [layoutNodes, setLayoutNodes] = useState([]);
  const [allNodes, setAllNodes] = useState([]); // Keep all nodes including merged ones

  // Expose canvas methods to parent
  useImperativeHandle(ref, () => ({
    fitNow: (animate) => canvasRef.current?.fitNow(animate),
    panBy: (deltaX, deltaY, animate) => canvasRef.current?.panBy(deltaX, deltaY, animate),
    panToPoint: (svgX, svgY, screenX, screenY, animate) => canvasRef.current?.panToPoint(svgX, svgY, screenX, screenY, animate),
    getTransform: () => canvasRef.current?.getTransform(),
    setTransform: (t, animate) => canvasRef.current?.setTransform?.(t, animate),
    markUserInteraction: () => canvasRef.current?.markInteraction?.(),
    zoomToPoint: (svgX, svgY, scale, animate) => canvasRef.current?.zoomToPoint(svgX, svgY, scale, animate),
    // Zoom to a node by ID - centers node on screen with nice zoom level
    zoomToNode: (nodeId, scale = 1.0, animate = true) => {
      const pos = positions[nodeId];
      if (!pos) {
        console.warn('zoomToNode: Node position not found for', nodeId);
        return false;
      }
      // Center on node center (accounting for card dimensions)
      const cardWidth = 360;
      const cardHeight = 90;
      const centerX = pos.x + cardWidth / 2;
      const centerY = pos.y + cardHeight / 2;
      canvasRef.current?.zoomToPoint(centerX, centerY, scale, animate);
      return true;
    },
    // Get position for a node ID
    getNodePosition: (nodeId) => positions[nodeId] || null
  }), [positions]);

  // zoomK is React state only so the unread dots can keep their screen size,
  // and they are rescaled imperatively per tick anyway. Setting it on every
  // wheel event re-rendered this whole component — every layer, hundreds of
  // arrows — per tick; now it settles once per gesture.
  const zoomSettleRef = useRef(null);
  const handleZoomChange = useCallback((transform) => {
    clearTimeout(zoomSettleRef.current);
    zoomSettleRef.current = setTimeout(() => setZoomK(transform.k), 150);
    onZoomChange?.(transform);
  }, [onZoomChange]);
  useEffect(() => () => clearTimeout(zoomSettleRef.current), []);

  // Calculate content bounds from positions for auto-fit zoom
  const contentBounds = useMemo(() => {
    const posValues = Object.values(positions);
    if (posValues.length === 0) return null;

    const xs = posValues.map(p => p.x);
    const ys = posValues.map(p => p.y);

    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys)
    };
  }, [positions]);

  // The seam of this layout: which node is a member of which merged root,
  // and what each root joins — for the marks and the family light.
  const seamRef = useRef({ memberOf: new Map(), roots: new Map(), isSame: [] });
  const seamMarks = useMemo(() => {
    const seam = seamRef.current; const marks = new Map();
    for (const [id, root] of seam.roots) marks.set(id, { rootId: id, colours: seamColours(root, scenarios), walls: root.wallCount, isRoot: true });
    for (const [memberId, rootId] of seam.memberOf) {
      const root = seam.roots.get(rootId);
      marks.set(memberId, { rootId, colours: seamColours(root, scenarios), walls: root.wallCount, isRoot: false });
    }
    return marks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutNodes, scenarios]);

  // Calculate layout when pathways change
  useEffect(() => {
    console.log('🎨 PathwayRenderer: Pathways changed, recalculating layout');
    console.log('📦 Input pathways:', pathways?.length || 0);

    if (!pathways || pathways.length === 0) {
      console.log('⚠️ No pathways to render');
      setPositions({});
      setArrows([]);
      setLayoutNodes([]);
      return;
    }

    // Merge multi-parent connections into each child's parentIds (PRIMARY first),
    // dropping redundant/cyclic borrowed edges. Layout still uses parentIds[0]
    // only (organicTree anti-reflow), so borrowed parents add dashed overlay
    // edges WITHOUT moving any node. Shared guard in utils/pathwayGraph.js — same
    // logic gates the manual add-parent + AI-offer flows.
    // See specs/systems-analysis-visualisation.md §9.
    // The seam first: sama asia links become merged roots, not second parents
    // (§4j). What is left of the connections goes on to be second parents.
    const seam = buildSeam(pathways, connections, { mutualOnly: seamMutualOnly, scenarios });
    seamRef.current = seam;
    const isSameKey = new Set(seam.isSame.map((c) => `${c.parentId || c.parent_node_id}->${c.childId || c.child_node_id}`));
    const treeConnections = connections.filter((c) => !isSameKey.has(`${c.parentId || c.parent_node_id}->${c.childId || c.child_node_id}`));
    const mergedPathways = mergeConnectionsIntoPathways(seam.pathways, treeConnections);

    // Transform pathways to layout format (includes building children arrays)
    const transformedAllNodes = pathwaysToLayoutFormat(mergedPathways);

    // Filter out nodes that have been merged into other nodes
    let nodes = transformedAllNodes.filter(node => !node.mergedInto);

    /**
     * Focus: the map redrawn around one kind of relation.
     *
     * Keeping only the nodes that take part would cut the tree into pieces —
     * the layout positions everything through its primary parent, so a node
     * whose parent is gone has nowhere to be. The chains back to the root come
     * with them, which is also the honest picture: a loop is not just its ring,
     * it is the ring and what feeds it.
     */
    if (focus) {
      const seeds = new Set();
      if (focus === 'loops') {
        loops.forEach((l) => (l.nodeIds || []).forEach((id) => seeds.add(id)));
      } else if (focus === 'blockers') {
        blockers.forEach((b) => {
          if (b.parentNodeId) seeds.add(b.parentNodeId);
          if (b.childNodeId) seeds.add(b.childNodeId);
          if (b.nodeId) seeds.add(b.nodeId);
        });
        blockerLinks.forEach((l) => {
          if (l.fromKind === 'node') seeds.add(l.fromId);
          if (l.toKind === 'node') seeds.add(l.toId);
        });
      } else if (focus === 'cross') {
        connections.forEach((c) => {
          seeds.add(c.parentId || c.parent_node_id);
          seeds.add(c.childId || c.child_node_id);
        });
      }
      if (seeds.size) {
        const parentOf = {};
        nodes.forEach((n) => { parentOf[n.id] = n.parentIds?.[0] || null; });
        const keep = new Set();
        seeds.forEach((id) => {
          let cur = id;
          let guard = 0;
          while (cur && !keep.has(cur) && guard < 60) {
            keep.add(cur);
            cur = parentOf[cur];
            guard += 1;
          }
        });
        nodes = nodes.filter((n) => keep.has(n.id));
      }
    }

    // Reading the map for what survives the obstacles: a blocked consequence
    // and everything under it never happens, so it is not drawn. Done before
    // the layout rather than after, so the map closes up around what is left
    // instead of leaving the space those branches used to take.
    if (hideBlockedDownstream && blockers.length) {
      const cut = buildCutNodes(nodes, blockers);
      // The blocked consequence itself stays: it is where the path ends, and
      // the blockers are drawn on the edge that reaches it — remove it and the
      // obstacles have nothing to sit on and vanish with it. Everything beyond
      // it goes, because none of that happens.
      const terminals = new Set(blockers.map((b) => b.childNodeId).filter(Boolean));
      if (cut.size) nodes = nodes.filter((n) => !cut.has(n.id) || terminals.has(n.id));
    }

    // Apply layout algorithm
    // The connector is furniture, and the layout has to pay for it. v1 put one
    // (+) in a 200-unit run; v2 adds a type pill and, where something blocks the
    // edge, a blocker card. Cramming them into the same run made them overlap,
    // so the columns move apart by however much the widest edge needs.
    // A blocked edge is given its own column (see organicTree): the card sits
    // where a node would and the child moves one level deeper. So the gap only
    // ever has to hold a (+) and a type pill, whatever blockers exist.
    const edgeBlockers = new Map();
    blockers.forEach((b) => {
      if (!b.parentNodeId || !b.childNodeId) return;
      const key = `${b.parentNodeId}->${b.childNodeId}`;
      edgeBlockers.set(key, [...(edgeBlockers.get(key) || []), b]);
    });
    const blockedEdges = new Set(edgeBlockers.keys());

    // How tall each blocked child's row has to be. The layout reserves this so
    // a stack of blockers pushes the rest of the tree apart instead of
    // overlapping the rows above and below it.
    const blockerHeights = new Map();
    edgeBlockers.forEach((list, key) => {
      const childId = key.split('->')[1];
      const height = blockerCardHeight(list, CARD_WIDTH);
      blockerHeights.set(childId, Math.max(blockerHeights.get(childId) || 0, height));
    });

    const layoutConfig = {
      cardWidth: CARD_WIDTH,
      cardHeight: 90,
      horizontalGap: COLUMN_GAP,
      verticalGap: 40,
      blockedEdges,
      blockerHeights,
    };

    console.log('🎯 Applying layout algorithm:', layoutType);
    /**
     * A blocking link has to run left to right like everything else, which
     * means whatever a blocker blocks belongs in a column after it. Where the
     * blockers land is only known once the map is laid out, so lay it out, read
     * the shortfall, push those subtrees deeper, and lay it out again. A push
     * can move another blocker, hence the loop — it settles in one or two
     * rounds, and the cap is there so a pathological cycle cannot spin.
     */
    const columnStep = CARD_WIDTH + layoutConfig.horizontalGap;
    const blockerById = new Map(blockers.map((b) => [b.id, b]));

    // Pushing a node moves its whole subtree, so a blocker that blocks one of
    // its own ancestors would push itself: every round the target moves right,
    // the blocker's parent moves with it, and the shortfall is the same as
    // before. Left alone that walked the map thousands of units to the right
    // and still never satisfied the constraint. Those links keep their honest
    // right-to-left line instead.
    const primaryParent = {};
    nodes.forEach((n) => { primaryParent[n.id] = n.parentIds?.[0] || null; });
    const isAncestorOf = (maybeAncestor, id) => {
      let cur = id;
      let guard = 0;
      while (cur && guard < 500) {
        if (cur === maybeAncestor) return true;
        cur = primaryParent[cur];
        guard += 1;
      }
      return false;
    };

    /**
     * Each blocker link asks for one node to sit a column further right:
     *
     *   blocker → node   the thing blocked goes after the blocker
     *   node → blocker   the blocker goes after the thing that feeds it
     *
     * Either way the node that actually moves is a node in the tree — for the
     * second case that is the blocker's own child, which carries the blocker
     * (and everything downstream of it) along.
     */
    const constraints = blockerLinks.map((l) => {
      if (l.fromKind === 'blocker' && l.toKind === 'node') {
        const blocker = blockerById.get(l.fromId);
        if (!blocker?.childNodeId) return null;
        return { push: l.toId, after: blocker.childNodeId, gap: 0 };
      }
      if (l.toKind === 'blocker' && l.fromKind === 'node') {
        const blocker = blockerById.get(l.toId);
        if (!blocker?.childNodeId) return null;
        // The blocker sits one step before its child, so clearing the source by
        // one column means clearing it by two from the child.
        return { push: blocker.childNodeId, after: l.fromId, gap: 2 };
      }
      return null;
    }).filter(Boolean)
      // Pushing a node moves its whole subtree, so a constraint whose anchor is
      // inside that subtree would push itself: every round the target moves
      // right, the anchor moves with it, and the shortfall never closes. Left
      // alone that walked the map thousands of units to the right. Those keep
      // an honest right-to-left line instead.
      .filter((c) => !isAncestorOf(c.push, c.after));
    let extraDepth = {};
    let laid = applyLayout(nodes, layoutType, { ...layoutConfig, extraDepth });

    for (let round = 0; round < 5 && constraints.length > 0; round += 1) {
      let moved = false;
      const next = { ...extraDepth };
      constraints.forEach(({ push, after, gap }) => {
        const anchorPos = laid.positions[after];
        const pushPos = laid.positions[push];
        if (!anchorPos || !pushPos) return;
        const mustClear = anchorPos.x + Math.max(1, gap) * columnStep;
        if (pushPos.x >= mustClear - 1) return;
        // Backstop: a chain of blocks should not be able to walk the map off to
        // the right, however tangled it gets.
        const want = (next[push] || 0) + Math.ceil((mustClear - pushPos.x) / columnStep);
        if (want > 8) return;
        next[push] = want;
        moved = true;
      });
      if (!moved) break;
      extraDepth = next;
      laid = applyLayout(nodes, layoutType, { ...layoutConfig, extraDepth });
    }

    const { positions: newPositions, arrows: newArrows } = laid;

    // Add strokeWidth to arrows based on target node's reactions (2px base + 0.5px per vote, capped at 8px)
    // Also add dashed style for arrows pointing to negatively voted nodes
    const cutNodes = buildCutNodes(nodes, blockers);
    const arrowsWithThickness = newArrows.map(arrow => {
      const targetNode = nodes.find(n => n.id === arrow.to);
      const reactions = targetNode?.reactions || 0;
      const baseStrokeWidth = 2;
      const strokeWidthPerVote = 0.5;
      const maxStrokeWidth = 8;
      const strokeWidth = Math.min(baseStrokeWidth + Math.abs(reactions) * strokeWidthPerVote, maxStrokeWidth);
      // Secondary parent = a borrowed (multi-parent) edge, i.e. not the node's
      // PRIMARY parent (parentIds[0]). It never affects layout (see organicTree
      // anti-reflow); here it's drawn dashed + faded + thinner so the causal
      // spine stays visually dominant. See specs/systems-analysis-visualisation.md §9.
      const isSecondary = (targetNode?.parentIds?.length > 1) && arrow.from !== targetNode.parentIds[0];
      // Dashed for negative votes, hidden intermediates ("Mine" mode), or secondary parents
      const dashed = reactions < 0 || !!targetNode?.hasHiddenAncestors || isSecondary;

      return decorateArrow(
        {
          ...arrow,
          isGlobalLink: isSecondary,
          strokeWidth: isSecondary ? 2 : strokeWidth,
          dashed,
          // Visible enough to follow across the map when they are switched on:
          // at 0.4 alpha and 1.5px they were technically drawn and practically
          // invisible, which is why they read as "cannot draw those at all".
          ...(isSecondary ? { stroke: 'rgba(150, 190, 245, 0.85)' } : {}),
        },
        targetNode,
        isSecondary,
        newPositions[arrow.from],
        newPositions[arrow.to],
        cutNodes,
      );
    });

    console.log('✅ Layout complete:', {
      positions: Object.keys(newPositions).length,
      arrows: arrowsWithThickness.length,
      nodes: nodes.length
    });

    setPositions(newPositions);
    setArrows(arrowsWithThickness);
    setLayoutNodes(nodes);
    setAllNodes(transformedAllNodes); // Store all nodes including merged ones
    // Edge decoration is part of this pass, so the colour toggle has to re-run it.
  }, [pathways, connections, layoutType, showEdgeTypes, colorEdgesByType, blockers, blockerLinks, hideBlockedDownstream, focus, loops, seamMutualOnly, scenarios, t]);

  // Get all descendants of a node (recursive)
  // NOTE: Only includes descendants that exist in the nodes array (not merged-away nodes)
  const getDescendants = (nodeId, nodes) => {
    const descendants = [];
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !node.children || node.children.length === 0) {
      return descendants;
    }

    node.children.forEach(childId => {
      // Only include child if it exists in nodes (skip merged-away nodes)
      if (nodes.find(n => n.id === childId)) {
        descendants.push(childId);
        descendants.push(...getDescendants(childId, nodes));
      }
    });

    return descendants;
  };

  // Handle node drag
  const handleNodeDrag = (phase, node, event) => {
    if (phase === 'start') {
      // Store initial positions for ALL nodes (for calculating delta)
      node._dragStartX = positions[node.id]?.x || 0;
      node._dragStartY = positions[node.id]?.y || 0;

      // Store original positions of all descendants
      const descendants = getDescendants(node.id, layoutNodes);
      node._descendantOriginalPositions = {};
      descendants.forEach(descId => {
        if (positions[descId]) {
          node._descendantOriginalPositions[descId] = { ...positions[descId] };
        }
      });
    } else if (phase === 'drag') {
      // Calculate how much the node has moved from START position
      const deltaX = event.x - node._dragStartX;
      const deltaY = event.y - node._dragStartY;

      // Get all descendants that should move with this node
      const descendants = getDescendants(node.id, layoutNodes);

      // Update positions for dragged node AND all its descendants
      setPositions(prev => {
        const newPositions = { ...prev };

        // Update the dragged node
        newPositions[node.id] = { x: event.x, y: event.y };

        // Update all descendants by the same delta from their ORIGINAL positions
        descendants.forEach(descId => {
          const originalPos = node._descendantOriginalPositions[descId];
          if (originalPos) {
            newPositions[descId] = {
              x: originalPos.x + deltaX,
              y: originalPos.y + deltaY
            };
          }
        });

        // Recalculate arrows in real-time using the NEW positions
        const cutNodes = buildCutNodes(layoutNodes, blockers);
        const newArrows = [];
        layoutNodes.forEach(n => {
          if (!n.parentIds || n.parentIds.length === 0) return;

          n.parentIds.forEach((parentId, parentIdx) => {
            const parentPos = newPositions[parentId];
            const childPos = newPositions[n.id];

            if (parentPos && childPos) {
              // Calculate strokeWidth based on target node's reactions
              const reactions = n.reactions || 0;
              const baseStrokeWidth = 2;
              const strokeWidthPerVote = 0.5;
              const maxStrokeWidth = 8;
              const strokeWidth = Math.min(baseStrokeWidth + Math.abs(reactions) * strokeWidthPerVote, maxStrokeWidth);
              const isSecondary = parentIdx > 0; // borrowed (multi-parent) edge
              const dashed = reactions < 0 || isSecondary; // Dashed for negative votes / secondary parents

              newArrows.push(decorateArrow(
                {
                  from: parentId,
                  to: n.id,
                  path: generateArrowPath(parentPos, childPos),
                  strokeWidth: isSecondary ? 1.5 : strokeWidth,
                  dashed,
                  ...(isSecondary ? { stroke: 'rgba(120, 160, 210, 0.4)' } : {}),
                },
                n,
                isSecondary,
                parentPos,
                childPos,
                cutNodes,
              ));
            }
          });
        });

        // Update arrows immediately (inside setPositions to use new positions)
        setArrows(newArrows);

        return newPositions;
      });
    } else if (phase === 'end') {
      // Check for collision with other nodes (merge on drop)
      const cardWidth = 360;
      const cardHeight = 90;
      const overlapThreshold = 0.5; // 50% overlap triggers merge

      let mergeTarget = null;

      console.log('🎯 Drag ended:', {
        nodeId: node.id,
        eventX: event.x,
        eventY: event.y,
        layoutPosition: positions[node.id]
      });

      // Check each node for collision (except the dragged node and its descendants)
      const descendants = getDescendants(node.id, layoutNodes);
      layoutNodes.forEach(otherNode => {
        if (otherNode.id === node.id) return; // Skip self
        if (descendants.includes(otherNode.id)) return; // Skip descendants
        if (otherNode.mergedInto) return; // Skip already merged nodes

        const otherPos = positions[otherNode.id];
        if (!otherPos) return;

        // Calculate overlap area
        // NOTE: Layout positions are top-left, but renderImpactPathNode centers cards
        // So actual card bounds are offset by cardWidth/2, cardHeight/2
        const draggedBounds = {
          left: event.x,
          right: event.x + cardWidth,
          top: event.y,
          bottom: event.y + cardHeight
        };

        const otherBounds = {
          left: otherPos.x,
          right: otherPos.x + cardWidth,
          top: otherPos.y,
          bottom: otherPos.y + cardHeight
        };

        // Calculate intersection
        const overlapLeft = Math.max(draggedBounds.left, otherBounds.left);
        const overlapRight = Math.min(draggedBounds.right, otherBounds.right);
        const overlapTop = Math.max(draggedBounds.top, otherBounds.top);
        const overlapBottom = Math.min(draggedBounds.bottom, otherBounds.bottom);

        if (overlapLeft < overlapRight && overlapTop < overlapBottom) {
          const overlapArea = (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
          const draggedArea = cardWidth * cardHeight;
          const overlapRatio = overlapArea / draggedArea;

          console.log(`  📏 Overlap with ${otherNode.id}:`, {
            draggedBounds,
            otherBounds,
            overlapArea,
            overlapRatio: (overlapRatio * 100).toFixed(1) + '%',
            threshold: (overlapThreshold * 100) + '%'
          });

          if (overlapRatio >= overlapThreshold) {
            console.log(`  ✅ MERGE TRIGGERED with ${otherNode.id}!`);
            mergeTarget = otherNode;
          }
        }
      });

      if (mergeTarget) {
        console.log('🔀 Merge target found:', mergeTarget.id);
      } else {
        console.log('❌ No merge target found');
      }

      // If collision detected, merge nodes
      if (mergeTarget && onNodeUpdate) {
        console.log('🔀 Merging', node.id, 'into', mergeTarget.id);

        // Merge logic: target node absorbs dragged node
        // IMPORTANT: Only pass merge-related fields to onNodeUpdate, not layout-specific fields
        // The pathways state uses 'text' and 'parentId', not 'title' and 'children'
        const mergedNode = {
          id: mergeTarget.id,
          members: [...(mergeTarget.members || []), node.id],
        };

        // Mark dragged node as merged
        const mergedAwayNode = {
          id: node.id,
          mergedInto: mergeTarget.id,
        };

        // Update both nodes
        onNodeUpdate(mergedNode);
        onNodeUpdate(mergedAwayNode);

        // Reassign children: all children of merged node should now point to group
        // Use allNodes (not layoutNodes) to find children, since merged node still exists there
        const descendants = getDescendants(node.id, allNodes);
        console.log('🔗 Reassigning children:', descendants, 'to', mergeTarget.id);

        descendants.forEach(descId => {
          const descNode = allNodes.find(n => n.id === descId);
          if (descNode && descNode.parentIds && descNode.parentIds.includes(node.id)) {
            // Update parentId to point to new group
            onNodeUpdate({
              id: descId,
              parentId: mergeTarget.id
            });
          }
        });

        console.log('✅ Merge complete:', {
          group: mergedNode.id,
          members: mergedNode.members,
          reassignedChildren: descendants.length
        });
      } else if (onNodeUpdate) {
        // No collision, check if node actually moved before saving
        const deltaX = Math.abs(event.x - node._dragStartX);
        const deltaY = Math.abs(event.y - node._dragStartY);
        const moved = deltaX > 5 || deltaY > 5; // Threshold to differentiate click from drag

        console.log('🔍 Drag end check:', {
          eventX: event.x,
          eventY: event.y,
          startX: node._dragStartX,
          startY: node._dragStartY,
          deltaX,
          deltaY,
          moved
        });

        if (moved) {
          console.log('💾 Node moved, saving position:', { deltaX, deltaY });
          // Only pass id and position, not layout fields
          onNodeUpdate({
            id: node.id,
            x: event.x,
            y: event.y
          });

          // Save all descendants
          const descendants = getDescendants(node.id, layoutNodes);
          descendants.forEach(descId => {
            const descPos = positions[descId];
            if (descPos) {
              onNodeUpdate({
                id: descId,
                x: descPos.x,
                y: descPos.y
              });
            }
          });
        } else {
          console.log('👆 Click detected (no movement), skipping position save');
        }
      }

      // Clean up drag state
      delete node._dragStartX;
      delete node._dragStartY;
      delete node._descendantOriginalPositions;
    }
  };

  // Handle node click (including split actions)
  // Memoised: it is a dependency of the node layer's rebuild, and a plain
  // function was a new one on every render of this component.
  const handleNodeClick = useCallback((node) => {
    // Check if this is a split action
    if (node.splitAction) {
      const { memberId, groupId } = node.splitAction;
      console.log('🔓 Split action:', { memberId, groupId });

      if (onNodeUpdate) {
        // Find the group node to update its members array
        const groupNode = layoutNodes.find(n => n.id === groupId);
        if (!groupNode) {
          console.error('❌ Group node not found:', groupId);
          return;
        }

        // Remove member from group's members array
        const updatedMembers = (groupNode.members || []).filter(id => id !== memberId);
        console.log('📝 Updated members:', updatedMembers);

        onNodeUpdate({
          id: groupId,
          members: updatedMembers
        });

        // Remove mergedInto from the split member (explicitly set to undefined)
        // This will make the node visible again when filtered
        onNodeUpdate({
          id: memberId,
          mergedInto: undefined
        });

        // Restore children: find all children that point to groupId and were originally from memberId
        // For simplicity, we'll just restore the split node's original children to point back to it
        // This requires tracking original parent relationships, which we don't have currently
        // So for now, we'll leave children pointing to the group

        console.log('✅ Split complete:', {
          splitNode: memberId,
          fromGroup: groupId,
          remainingMembers: updatedMembers.length
        });
      }
    } else if (onNodeClick) {
      // Regular node click
      onNodeClick(node);
    }
  }, [onNodeClick, onNodeUpdate, layoutNodes]);

  // Helper to generate arrow path routing through (+) button
  // The type lives on the child row for spine edges (submissions.effect_type);
  // borrowed parents carry theirs on the connection row instead.
  // The same card and gutter the layout used: route connectors turn inside the gutter (layouts/route.js)
  const routeConfig = { cardWidth: CARD_WIDTH, horizontalGap: COLUMN_GAP };
  const decorateArrow = (arrow, targetNode, isSecondary, fromPos, toPos, cutNodes = new Set()) => {
    // Valinnat (§4b): the connector into a consequence takes that consequence's
    // valence. A path that leads somewhere the room does not want IS an
    // unwanted path — colouring only the cards would leave the route between
    // them neutral, and the route is the thing a person traces with a finger.
    // Wins over the effect-type stroke when both layers are on: the type is
    // still readable on the pill, and the branch reading is what the switch was
    // turned on for.
    const valenceStroke = targetNode?.valence
      ? valenceStyle(targetNode.valence).line
      : null;
    const base = valenceStroke ? { ...arrow, stroke: valenceStroke } : arrow;
    // Root → scenario is structure, not a claim about cause; it carries no type.
    if (!showEdgeTypes || (targetNode?.depth || 0) === 0) return base;
    const stored = isSecondary
      ? connections.find((c) => c.parentId === arrow.from && c.childId === arrow.to)?.type
      : targetNode?.effectType;
    // Every edge already means "leads to" — that is what an untyped impact-path
    // connector has always meant. Showing it says out loud what the map already
    // says, so existing maps read as complete instead of unlabelled, and the
    // work becomes correcting the few that are something else rather than
    // labelling all 324. Drawn muted, because it is the map's assumption and
    // not yet anyone's decision.
    const isDefault = !effectColor(stored);
    const type = isDefault ? DEFAULT_EFFECT_TYPE : stored;
    const color = effectColor(type);
    if (!color) return arrow;
    const label = t(`impactMapV2.effectTypes.${type}`);
    // One edge, one statement: a type colours its own connector and the node it
    // lands on, and nothing further. Inheriting down the branch made a single
    // change repaint everything below it, which read as the edit having done
    // more than it did.
    const decorated = {
      ...base,
      // The stored value, so CrossLinkLayer can show and re-pick it without
      // looking the connection up a second time.
      effectType: isDefault ? null : stored,
      label,
      labelMuted: isDefault,
      blockers: blockers.filter((b) => b.parentNodeId === arrow.from && b.childNodeId === arrow.to),
      blockerColumn: arrow.blockerColumn || null,
      labelColor: colorEdgesByType ? color : 'rgba(255,255,255,0.6)',
      avoids: targetNode?.valence === 'unwanted', // the knot before a bad consequence is green
    };
    if (colorEdgesByType && !isDefault && !valenceStroke) decorated.stroke = color;
    if (valenceStroke) decorated.stroke = valenceStroke;
    // Blocked flow reads as blocked: the connector goes dashed and red past the
    // knot rather than continuing as if nothing stops it.
    if (decorated.blockers.length > 0) {
      // PathwayArrow splits this edge itself: own colour up to the blocker card,
      // red past it. Leave the stroke alone.
    } else if (cutNodes.has(arrow.to)) {
      // Downstream of a blocker: drawn cut, not merely tinted.
      decorated.dashed = true;
      decorated.stroke = CUT_COLOR;
      decorated.labelColor = CUT_COLOR;
      decorated.labelMuted = true;
    }
    // The pill is measured on the line as drawn (PathwayArrow), never placed from
    // positions: a placed pill floated wherever the drawn line had moved away.
    // Route connectors share a vertical beside the parent, so their word goes near the end.
    if (layoutType === LAYOUT_TYPES.ROUTE) decorated.labelAtEnd = true;
    return decorated;
  };

  const generateArrowPath = (from, to) => {
    // Route layout: the chain's branches at right angles (layouts/route.js)
    if (layoutType === LAYOUT_TYPES.ROUTE) return routeArrowPath(from, to, routeConfig);
    const config = {
      cardWidth: 360,
      cardHeight: 90,
      buttonGap: 36  // Distance from node edge to (+) button center
    };

    const fromCenterX = from.x;
    const fromCenterY = from.y;
    const toCenterX = to.x;
    const toCenterY = to.y;

    // Start point: Right edge of parent node
    const startX = fromCenterX + config.cardWidth / 2;
    const startY = fromCenterY;

    // Waypoint: Parent's (+) button position
    const buttonX = startX + config.buttonGap;
    const buttonY = startY;

    // End point: Left edge of child node
    const endX = toCenterX - config.cardWidth / 2;
    const endY = toCenterY;

    // Create path with two Bezier segments routing through the (+) button
    // Segment 1: From parent edge to (+) button
    const segment1ControlX = startX + config.buttonGap / 2;
    const segment1ControlY = startY;

    // Segment 2: From (+) button to child edge
    const horizontalDistance = endX - buttonX;
    const segment2Control1X = buttonX + horizontalDistance / 3;
    const segment2Control1Y = buttonY;
    const segment2Control2X = endX - horizontalDistance / 3;
    const segment2Control2Y = endY;

    // Build the path: Move to start, line to button, then Bezier curve to end
    return `M ${startX},${startY} L ${buttonX},${buttonY} C ${segment2Control1X},${segment2Control1Y} ${segment2Control2X},${segment2Control2Y} ${endX},${endY}`;
  };

  // Apply the transient offsets for rendering only. Arrows are re-pathed so the
  // connectors stretch with the nodes instead of detaching from them.
  // Give each card the colour of the edge that reaches it, so the relation and
  // its consequence read as one statement rather than a coloured line pointing
  // at a neutral box.
  const nodesWithBorders = useMemo(() => {
    if (!showEdgeTypes) return layoutNodes;
    const cutNodes = buildCutNodes(layoutNodes, blockers);
    // A loop is a claim about a set of nodes, so the set should look like one
    // thing. Its members take the loop's colour — above the edge type, which
    // describes a single relation, and below a cut, which stops everything.
    const loopMembers = new Map();
    loops.forEach((loop) => {
      (loop.nodeIds || []).forEach((id) => { if (!loopMembers.has(id)) loopMembers.set(id, loopColor(loop)); });
    });
    return layoutNodes.map((n) => {
      // Cut wins over type: a blocked consequence is blocked whatever the
      // relation that would have produced it.
      if (cutNodes.has(n.id)) return { ...n, borderColor: CUT_COLOR, isCut: true };
      const suggested = suggestedNodeIds?.has(n.id) || false;
      if (loopMembers.has(n.id)) {
        return { ...n, borderColor: loopMembers.get(n.id), inLoop: true, hasSuggestion: suggested };
      }
      const color = colorEdgesByType ? effectColor(n.effectType) : null;
      if (color) return { ...n, borderColor: color, hasSuggestion: suggested };
      return suggested ? { ...n, hasSuggestion: true } : n;
    });
  }, [layoutNodes, showEdgeTypes, colorEdgesByType, blockers, loops, suggestedNodeIds]);


  /**
   * Routes that the tree has no column for need lanes of their own.
   *
   * Both a loop's closing edge and a cross-branch link join two nodes the
   * layout never put next to each other. Rather than drape them over whatever
   * lies between, the map opens a clear horizontal band above a row and pushes
   * the block above it further up — the way a transit map gives a crossing line
   * its own track instead of letting it cut through stations.
   *
   * A loop needs one lane, above the higher of its two ends. A cross-branch
   * link needs two: one above its source and one above its target, joined by a
   * vertical lane in the gap between two columns, where the only things to
   * cross are spine connectors — and those get hopped over.
   *
   * Vertical shifts only. No node changes parent and no column moves, so the
   * anti-reflow rule still holds.
   */
  const LANE_BAND = 56;
  // A pill is 24 tall, so lanes any closer than this stacked their labels on
  // top of each other even though the lines themselves stayed apart.
  const SUBLANE_STEP = 32;
  const routeLanes = useMemo(() => {
    const offsets = {};
    const lanes = {};
    const crossLinks = showGlobalLinks
      ? arrows.filter((a) => a.isGlobalLink && positions[a.from] && positions[a.to])
      : [];
    if ((!loops.length && !crossLinks.length) || !Object.keys(positions).length) {
      return { offsets, lanes, crossLinks };
    }

    const heightOf = (id) => nodeCardHeight(layoutNodes.find((n) => n.id === id));
    const centreOf = (id) => (positions[id] ? positions[id].y + (offsets[id] || 0) : null);
    const topOf = (id) => centreOf(id) - heightOf(id) / 2;

    // One request per lane the map has to open, anchored on the row it sits
    // above. Widest routes first: a long one's lane would otherwise be shoved
    // around by the short ones sharing its rows.
    const requests = [];
    loops.forEach((loop) => {
      if (!positions[loop.closingFromId] || !positions[loop.closingToId]) return;
      requests.push({
        kind: 'loop',
        key: `loop:${loop.id}`,
        span: Math.abs(positions[loop.closingFromId].x - positions[loop.closingToId].x),
        anchors: [{ slot: 'laneA', over: topOf(loop.closingFromId) <= topOf(loop.closingToId) ? loop.closingFromId : loop.closingToId }],
        loop,
      });
    });
    crossLinks.forEach((link) => {
      // "Sama asia" between two cards in one column is drawn as a straight
      // line between them and needs no lane — asking for one pushed the two
      // cards apart to make room for a route that then ran round the map.
      if (normalizeEffectType(link.effectType) === 'is_same'
        && isStraightPair(link.from, link.to, positions, layoutNodes)) return;
      // One lane each: the route climbs out of the source's row, runs along it,
      // then drops straight into the target's own incoming corridor. The target
      // end needs no lane of its own because it arrives where the spine does.
      requests.push({
        kind: 'cross',
        key: `${link.from}->${link.to}`,
        span: Math.abs(positions[link.from].x - positions[link.to].x)
          + Math.abs(positions[link.from].y - positions[link.to].y),
        anchors: [{ slot: 'laneA', over: link.from }],
        link,
      });
    });
    requests.sort((a, b) => b.span - a.span);

    /**
     * Several routes want a lane above the same row, and they have to queue in
     * a fixed order or they land on each other. The loop takes the innermost
     * lane — it belongs to the two cards it joins, so it should hug them — and
     * cross-branch links stack outwards above it.
     *
     * Rows are grouped by a coarse key, not an exact top: two cards in one row
     * have different heights (a card with evaluation tags is 110, a plain one
     * 90), so their tops differ by ten and an exact key put two routes in
     * "different" bands at the same height, which is precisely how a loop's
     * pill ended up underneath a cross-link's.
     */
    const BAND_GRID = 60;
    const bandKeyFor = (anchorId) => Math.round((topOf(anchorId)) / BAND_GRID);

    const KIND_ORDER = { loop: 0, cross: 1 };
    const slots = new Map();
    const byBand = new Map();
    requests.forEach((req) => {
      req.anchors.forEach((a) => {
        const key = `${bandKeyFor(a.over)}`;
        if (!byBand.has(key)) byBand.set(key, []);
        byBand.get(key).push({ req, anchor: a });
      });
    });
    byBand.forEach((entries) => {
      entries
        // Narrower first: the loop that joins two neighbours takes the lane
        // nearest the row, and the one spanning the map goes round it.
        .sort((x, y) => (KIND_ORDER[x.req.kind] - KIND_ORDER[y.req.kind]) || (x.req.span - y.req.span))
        .forEach((entry, i) => { slots.set(`${entry.req.key}:${entry.anchor.slot}`, i); });
    });

    // The run sits in the row gap above its anchor. The vertical legs ride
    // the column gaps (LoopEdgeLayer / CrossLinkLayer), so nothing between
    // the two ends is in the run's way: the cards in higher rows are above
    // it, the cards in its own row below it. An earlier version lifted the
    // run over every card between the ends, which walked a loop anchored on
    // row five up into row one's band — on top of the loop that lived there.
    const openLane = (anchorId, depth) => {
      const anchorTop = topOf(anchorId);
      const laneY = anchorTop - LANE_BAND / 2 - 10 - depth * SUBLANE_STEP;
      const bandTop = laneY - LANE_BAND / 2;

      let needed = 0;
      const above = [];
      layoutNodes.forEach((n) => {
        const centre = centreOf(n.id);
        if (centre == null || centre >= laneY) return;
        above.push(n.id);
        needed = Math.max(needed, centre + heightOf(n.id) / 2 - bandTop);
      });
      if (needed > 0) above.forEach((id) => { offsets[id] = (offsets[id] || 0) - needed; });
      return { anchorId, depth };
    };

    /**
     * One allocator, both axes. A lane is a place in a queue for a shared
     * corridor: horizontal lanes queue above a row, vertical lanes queue inside
     * a column gap, and either way the second route to ask gets a different
     * one. Anything routed added later has to ask the same allocator, which is
     * what keeps this from becoming a bug that is fixed over and over.
     */
    // Twelve lanes, 14 px apart: a prefilled team with 10–20 incoming links overflowed
    // four lanes and the fifth link landed on the first one's trunk, unreadable.
    const VERTICAL_STEP = 14;
    const VERTICAL_LANES = 12;
    const verticalUsed = new Map();
    const verticalLane = (targetX, gap) => {
      const key = Math.round(targetX);
      const i = verticalUsed.get(key) || 0;
      verticalUsed.set(key, i + 1);
      return targetX - (CARD_WIDTH / 2 + gap * 0.25) - (i % VERTICAL_LANES) * VERTICAL_STEP;
    };

    const opened = [];
    requests.forEach((req) => {
      const reqSlots = {};
      req.anchors.forEach((a) => {
        reqSlots[a.slot] = openLane(a.over, slots.get(`${req.key}:${a.slot}`) || 0);
      });
      opened.push({ req, slots: reqSlots });
    });

    // A later lane lifts everything above it — including earlier cards and the
    // lanes cut for them. The bands stay clear because the whole block moves
    // together, but the recorded y does not, so read them back off the final
    // positions.
    opened.forEach(({ req, slots }) => {
      const lane = {};
      Object.entries(slots).forEach(([slot, { anchorId, depth }]) => {
        lane[slot] = topOf(anchorId) - LANE_BAND / 2 - 10 - depth * SUBLANE_STEP;
      });
      if (req.kind === 'loop') {
        // The arc's two vertical runs ride the column gaps — up in the gap
        // right of the source, down in the gap left of the target — from the
        // same queue the cross-links use. They used to rise straight out of
        // the source's top and drop straight onto the target's, which is
        // through every card stacked above either in its column.
        const gap = showEdgeTypes ? 340 : 200;
        const fromX = positions[req.loop.closingFromId].x;
        const toX = positions[req.loop.closingToId].x;
        lane.riseX = verticalLane(fromX + CARD_WIDTH + gap, gap);
        lane.dropX = verticalLane(toX, gap);
      }
      if (req.link) {
        // The vertical lane runs in the column gap left of the target — the one
        // place between them with no cards in it. Not the middle of that gap:
        // the type pill sits there. And not a fixed x either: every route into
        // the same column used to get the identical lane and they drew straight
        // over each other. They queue now, the same way the horizontal bands
        // do.
        const tx = positions[req.link.to].x;
        const gap = showEdgeTypes ? 340 : 200;
        lane.laneX = verticalLane(tx, gap);
      }
      lanes[req.key] = lane;
    });

    return { offsets, lanes, crossLinks };
  }, [loops, positions, layoutNodes, arrows, showGlobalLinks, showEdgeTypes]);

  const loopChannels = useMemo(() => ({
    offsets: routeLanes.offsets,
    channels: Object.fromEntries(
      Object.entries(routeLanes.lanes)
        .filter(([key]) => key.startsWith('loop:'))
        .map(([key, lane]) => [key.slice(5), { y: lane.laneA, riseX: lane.riseX, dropX: lane.dropX }]),
    ),
  }), [routeLanes]);

  // Lanes push the upper block further up, so the frame has to grow with it or
  // the auto-fit crops the rows it just moved.
  const fitBounds = useMemo(() => {
    if (focusChain?.length >= 2) {
      const step = CARD_WIDTH + COLUMN_GAP;
      // PathwayCanvas frames these bounds as if they were card TOP-LEFTS — it
      // adds a card's width and height before centring — but every position in
      // this layout is a card CENTRE. On the whole map that half-card is lost
      // in the margins; with two cards on screen it is a visible 180 units of
      // drift, so the bounds are handed over shifted to the convention the fit
      // expects.
      const half = { x: CARD_WIDTH / 2, y: 45 };
      return {
        minX: -half.x,
        maxX: (focusChain.length - 1) * step - half.x,
        minY: -half.y,
        maxY: -half.y,
      };
    }
    if (!contentBounds) return contentBounds;
    const shifts = Object.values(routeLanes.offsets);
    if (!shifts.length) return contentBounds;
    return { ...contentBounds, minY: contentBounds.minY + Math.min(0, ...shifts) };
  }, [contentBounds, routeLanes, focusChain, showEdgeTypes]);

  /**
   * Focused view: one relation, nothing else.
   *
   * Dimming the rest helps on a map of eighty cards; it does not stop the two
   * cards being a screen apart. Here the chain is laid out side by side in the
   * order the relation runs — for a loop that is its whole ring — and the
   * canvas fits to it.
   */
  const focusView = useMemo(() => {
    if (!focusChain?.length || focusChain.length < 2) return null;
    let known = focusChain.filter((id) => layoutNodes.some((n) => n.id === id));
    if (known.length < 2) return null;
    // A loop's chain is a ring; lay it out from the card the closing link
    // lands on to the card it leaves, so every step left to right is a real
    // edge and the closing link is the arc drawn back over the top. Started
    // anywhere else, the closing link fell mid-row as a plain arrow.
    if (focusLoop && known.includes(focusLoop.closingToId)) {
      const i = known.indexOf(focusLoop.closingToId);
      known = [...known.slice(i), ...known.slice(0, i)];
    }
    const step = CARD_WIDTH + COLUMN_GAP;
    const positionsOut = {};
    known.forEach((id, i) => { positionsOut[id] = { x: i * step, y: 0 }; });
    return {
      ids: known,
      positions: positionsOut,
      nodes: known.map((id) => layoutNodes.find((n) => n.id === id)),
    };
  }, [focusChain, focusLoop, layoutNodes, showEdgeTypes]);

  // The content changed wholesale; frame it. And on the way back, return to
  // where the person was: "Takaisin karttaan" means the view they left, not
  // the whole map from orbit.
  const focusKey = focusChain?.join('|') || '';
  const viewBeforeFocusRef = useRef(null);
  const wasFocusedRef = useRef(false);
  useEffect(() => {
    const entering = Boolean(focusKey) && !wasFocusedRef.current;
    const leaving = !focusKey && wasFocusedRef.current;
    wasFocusedRef.current = Boolean(focusKey);
    if (entering) viewBeforeFocusRef.current = canvasRef.current?.getTransform?.() || null;
    const id = setTimeout(() => {
      if (leaving && viewBeforeFocusRef.current) {
        canvasRef.current?.setTransform?.(viewBeforeFocusRef.current, true);
        viewBeforeFocusRef.current = null;
      } else if (focusKey) {
        canvasRef.current?.fitNow?.(true);
      }
    }, 60);
    return () => clearTimeout(id);
  }, [focusKey]);

  const hasYOffsets = Object.keys(loopChannels.offsets).length > 0;
  const hasOffsets = (nodeOffsets && Object.keys(nodeOffsets).length > 0) || hasYOffsets;

  const displayPositions = useMemo(() => {
    if (focusView) return focusView.positions;
    if (!hasOffsets) return positions;
    const out = {};
    Object.entries(positions).forEach(([id, pos]) => {
      const dx = (nodeOffsets && nodeOffsets[id]) || 0;
      const dy = loopChannels.offsets[id] || 0;
      out[id] = (dx || dy) ? { ...pos, x: pos.x + dx, y: pos.y + dy } : pos;
    });
    return out;
  }, [positions, nodeOffsets, loopChannels, hasOffsets, focusView]);

  // Cross-branch links leave the arrow layer entirely: CrossLinkLayer routes
  // them through the lanes reserved above, which a bezier between two card
  // centres cannot do.
  const visibleArrows = useMemo(() => arrows.filter((a) => !a.isGlobalLink), [arrows]);

  const displayArrows = useMemo(() => {
    if (focusView) return focusArrows(focusView, arrows, { focusLoop, focusLabel, focusColor, arrowPath: generateArrowPath });
    if (!hasOffsets) return visibleArrows;
    return visibleArrows.map((a) => {
      const from = displayPositions[a.from];
      const to = displayPositions[a.to];
      if (!from || !to) return a;
      const dy = loopChannels.offsets[a.to] || 0;
      return {
        ...a,
        path: generateArrowPath(from, to),
        blockerColumn: a.blockerColumn && dy
          ? { ...a.blockerColumn, y: a.blockerColumn.y + dy }
          : a.blockerColumn,
      };
    });
  }, [visibleArrows, arrows, displayPositions, loopChannels, hasOffsets, focusView, focusLoop, focusLabel, focusColor]);

  // Where each blocker card actually sits, so the link layer can put the same
  // handle on it that every node card has. The arrows compute this column; this
  // reads it back out rather than recomputing the geometry a second time.
  const blockerBoxes = useMemo(() => {
    if (!showEdgeTypes || !blockers.length) return [];
    const out = [];
    displayArrows.forEach((arrow) => {
      if (!arrow.blockerColumn) return;
      const onEdge = blockers.filter((b) => b.parentNodeId === arrow.from && b.childNodeId === arrow.to);
      if (!onEdge.length) return;
      // One box per blocker: each card is its own object, so each gets its own
      // link handle rather than the whole stack sharing one.
      const stack = blockerStackLayout(onEdge, 360);
      stack.cards.forEach((card) => {
        out.push({
          id: card.blocker.id,
          blocker: card.blocker,
          lines: card.lines,
          x: arrow.blockerColumn.x,
          y: arrow.blockerColumn.y + card.cy,
          width: stack.width,
          height: card.height,
        });
      });
    });
    return out;
  }, [displayArrows, blockers, showEdgeTypes]);

  // Resolved to boxes because a blocker card has no entry in `positions` — it
  // is not a node, it just sits in a node column.
  const blockLinkBoxes = useMemo(() => {
    if (!blockerLinks.length || !blockerBoxes.length) return [];
    const boxById = new Map(blockerBoxes.map((b) => [b.id, b]));
    // Either end can be the blocker: a blocker blocking a consequence, or a
    // consequence feeding a blocker. Both are one line on the map.
    const nodeBox = (id) => {
      const pos = displayPositions[id];
      if (!pos) return null;
      const node = nodesWithBorders.find((n) => n.id === id);
      return { x: pos.x, y: pos.y, width: 360, height: nodeCardHeight(node) };
    };
    return blockerLinks
      .map((l) => {
        const from = l.fromKind === 'blocker' ? boxById.get(l.fromId) : nodeBox(l.fromId);
        const to = l.toKind === 'blocker' ? boxById.get(l.toId) : nodeBox(l.toId);
        if (!from || !to) return null;
        return {
          key: `block:${l.id || `${l.fromId}->${l.toId}`}`,
          from,
          to,
          // Only a link *out of* a blocker is a block. A link *into* one is an
          // ordinary causal claim — this consequence feeds that obstacle — and
          // saying "estää" on it read as the consequence blocking the blocker.
          blocks: l.fromKind === 'blocker',
          effectType: l.effectType,
        };
      })
      .filter(Boolean);
  }, [blockerLinks, blockerBoxes, displayPositions, nodesWithBorders]);

  const crossLinkProps = {
    links: focusView ? [] : routeLanes.crossLinks,
    blockLinks: focusView ? [] : blockLinkBoxes,
    nodes: nodesWithBorders,
    positions: displayPositions,
    lanes: routeLanes.lanes,
    spineArrows: displayArrows,
    colorEdgesByType,
    onTypeClick: onCrossLinkTypeClick,
    selection,
    onSelect: onSelectEdge,
  };

  return (
    <PathwayCanvas
      ref={canvasRef}
      width={width}
      height={height}
      onZoomChange={handleZoomChange}
      contentBounds={fitBounds}
      onBackgroundClick={() => onSelectEdge?.(null)}
    >
      {/* Nodes glide to their new spot when the layout stretches; dragging opts
          out so the dragged card still tracks the cursor exactly. */}
      {animatePositions && (
        <style>{`
          .node { transition: transform 0.24s cubic-bezier(0.2, 0.9, 0.3, 1); }
          .node.dragging { transition: none; }
          .im-glide-fade { animation: im-glide-fade 0.9s ease both; }
          @keyframes im-glide-fade { 0%, 55% { opacity: 0; } 100% { opacity: 1; } }
        `}</style>
      )}

      {/* Cluster connections render first (behind everything) */}
      <ClusterConnectionLayer
        clusters={clusters}
        positions={displayPositions}
        enabled={showClusterConnections}
      />

      {/* Arrows render behind nodes */}
      {/* Route layout: links between towns are freeform curves behind everything */}
      {/* Faded per line (CrossLinkLayer freeform), not by group opacity: a group's opacity is composited off-screen on every zoom frame */}
      <g key={glideKey ?? 'map'} className={glideKey ? 'im-glide-fade' : undefined}>
      {layoutType === LAYOUT_TYPES.ROUTE && <CrossLinkLayer {...crossLinkProps} freeform />}
      <PathwayArrowLayer
        showLabels={zoomK >= 0.5}
        onLabelClick={onEdgeLabelClick}
        onKnotClick={onEdgeKnotClick}
        arrows={displayArrows}
        selection={selection}
        onSelect={onSelectEdge}
      />
      </g>

      {/* Nodes render on top */}
      <ChoiceBoxLayer
        show={showChoices && !focusView}
        choicePoints={choicePoints}
        nodes={nodesWithBorders}
        positions={positions}
      />
      <PathwayNodeLayer
        choicePointIds={choicePointIds}
        nodes={focusView ? focusView.nodes : nodesWithBorders}
        positions={displayPositions}
        litNodeIds={focusView ? null : (selection?.ids || null)}
        seamMarks={seamMarks}
        onSeamTap={onSeamTap}
        onNodeClick={handleNodeClick}
        onAddConsequenceClick={onAddConsequenceClick}
        showAddButton={showAddButtons && !focusView}
        currentParticipantId={currentParticipantId}
        highlightedNodeId={highlightedNodeId}
        processingNodeIds={processingNodeIds}
        pulsingNodeId={pulsingNodeId}
        freshHighlightNodeIds={freshHighlightNodeIds}
        nodeTypeLabels={nodeTypeLabels}
        zoomK={zoomK}
        glideKey={glideKey}
        // onNodeDrag={handleNodeDrag}  // Temporarily disabled to fix click vs drag conflict
      />
      {focusView && <FocusTownCaptions nodes={focusView.nodes} positions={displayPositions} scenarios={scenarios} />}

      {/* Everything that is not the connector being looked at recedes. Every
          overlay lives in here, so nothing can quietly stay lit by sitting
          outside the group — which is exactly how the blocker cards did. */}
      <g style={{ transition: 'opacity 0.18s ease' }}>
      {/* Blockers are nodes in the blocker column: the layout places them, this
          draws them, and the handles and routed lines read the same boxes. */}
      <BlockerLayer
        boxes={focusView ? [] : blockerBoxes}
        onBlockerClick={onBlockerClick}
        litIds={selection?.ids || null}
      />
      {/* Cross-branch links, routed through their lanes (route layout: drawn at the back instead). */}
      {layoutType !== LAYOUT_TYPES.ROUTE && <CrossLinkLayer {...crossLinkProps} />}

      {/* The closing edge, drawn as the return arc it is — with its type pill
          and the loop's R/B mark on it. */}
      <LoopEdgeLayer
        loops={focusView ? (focusLoop ? [focusLoop] : []) : loops}
        connections={connections}
        nodes={nodesWithBorders}
        positions={displayPositions}
        // The lanes belong to the whole map's layout; in the focus view the
        // arc falls back to riding just above the two cards.
        channels={focusView ? {} : loopChannels.channels}
        onLoopClick={onLoopClick}
        selection={selection}
        onSelect={onSelectEdge}
      />
      </g>

      {/* Above the cards: the handle has to be grabbable where the card is. */}
      <LoopLinkLayer
        nodes={nodesWithBorders}
        positions={displayPositions}
        blockerBoxes={blockerBoxes}
        enabled={showLinkHandles && !focusView}
        onLink={onDrawLink}
      />
    </PathwayCanvas>
  );
});

export default PathwayRenderer;
